import { Card } from "../components/Card";
import { Cta } from "../components/Cta";
import { Section } from "../components/Section";
import { GAMBLING_HELP } from "../content/compliance";
import { useAuth } from "../features/auth/context";
import { selectFullList } from "../features/fixture/landing";
import { useFixturesPublic } from "../features/fixture/useFixturesPublic";
import { usePageMeta } from "../lib/usePageMeta";
import { FixtureListSection, FixtureListSkeleton } from "./FixtureListSection";
import styles from "./Home.module.css";

/**
 * "See more" from the landing page — `/fixtures`, PUBLIC, same anonymous
 * file as `Home.tsx` (`useFixturesPublic`), the same grouping and row
 * markup (`FixtureListSection`), just a bigger window: `selectFullList`
 * instead of `selectLanding` — about 20 fixtures, or everything inside 48
 * hours if that is more. See `features/fixture/landing.ts` for the shared
 * rule both pages read.
 *
 * Its own route rather than a query flag on `/` so it can carry its own
 * canonical, appear once in the sitemap under its own honest description,
 * and be a link a reader can actually navigate to and back from — see
 * `config/routes.ts` and `content/meta.ts`'s `allFixtures` entries.
 *
 * Eager, not lazy, for the same reason `Home`/`Landing`/`Pricing` etc. are
 * (App.tsx's own note): everything a signed-out visitor can reach stays out
 * of the authenticated chunk split.
 */
export function AllFixtures() {
  usePageMeta("allFixtures");
  const { status } = useAuth();
  const fixtures = useFixturesPublic();

  const now = new Date();
  const list = selectFullList(fixtures.data?.fixtures ?? [], now);

  return (
    <Section eyebrow="Fixtures" heading="Every fixture Kev's calling" tone="ink" level={1}>
      {/* Ned, 2026-09-08. This used to open by reciting the windowing rule —
          "the next 20 or so, or everything kicking off in the next two days if
          that's more". That sentence spent the reader's attention on how the
          list is BUILT before saying what is in it, and it was about to go
          stale anyway: the window became per-sport the same day, so "the next
          20" stopped being one number about one list. What the page offers is
          the thing to lead with. */}
      <p className={styles.standfirst}>
        Kev&rsquo;s read of everything upcoming. Team news, the head-to-head,
        player props and where he&rsquo;s found value. Open a match for the full
        breakdown.
      </p>

      {status !== "authenticated" && (
        <Card className={styles.signIn}>
          <p className={styles.signInText}>
            Kev&rsquo;s head to head winning chances and fair odds need a free account.
          </p>
          <div className={styles.signInActions}>
            <Cta to="/signup">Create free account</Cta>
            <Cta to="/login" variant="secondary">
              Sign in
            </Cta>
          </div>
        </Card>
      )}

      {fixtures.isPending && <FixtureListSkeleton />}

      {fixtures.isError && (
        <p className={styles.muted}>The fixture list is unavailable right now.</p>
      )}

      {fixtures.isSuccess && list.length === 0 && (
        <Card className={styles.card}>
          <p className={styles.muted}>
            <strong>Nothing on right now.</strong> The card fills as Kev freezes his calls before kick-off.
          </p>
        </Card>
      )}

      <FixtureListSection
        fixtures={list}
        now={now}
        droppedCount={fixtures.data?.droppedFixtures?.length ?? 0}
      />

      <div className={styles.finePrint}>
        <p>
          Kev&rsquo;s read is his own, never a bookmaker&rsquo;s. Open a match
          for the full case, and sign in free to see the numbers behind it.
        </p>
        <p>
          18+. {GAMBLING_HELP.name} {GAMBLING_HELP.phone}. {GAMBLING_HELP.note}
        </p>
      </div>
    </Section>
  );
}
